import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Eye } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { fmtIST } from "@/lib/utils";

const FIELDS = [
  ["missed_reason", "Reason for missed class(es)"],
  ["problems", "Problems faced"],
  ["academic_activity", "Academic activity"],
  ["admin_assigned", "Administrative task assigned"],
  ["admin_done", "Administrative task done"],
  ["advancement", "Self academic advancement (time spent)"],
  ["improvement", "Substantial improvement suggested/done"],
];

export default function DailyReports() {
  const { user } = useAuth();
  const isTeacher = user?.role === "teacher";
  const [date, setDate] = useState("");
  const [deptId, setDeptId] = useState("all");
  const [view, setView] = useState(null);

  const { data: mine = [] } = useQuery({
    queryKey: ["my-daily-reports"],
    queryFn: async () => (await api.get("/staff/my-daily-reports")).data,
    enabled: isTeacher,
  });
  const { data: departments = [] } = useQuery({
    queryKey: ["departments"],
    queryFn: async () => (await api.get("/admin/departments")).data,
    enabled: !isTeacher,
  });
  const { data: all = [] } = useQuery({
    queryKey: ["all-daily-reports", date, deptId],
    queryFn: async () => (await api.get("/staff/daily-reports", {
      params: { ...(date ? { date_str: date } : {}), ...(deptId !== "all" ? { department_id: deptId } : {}) },
    })).data,
    enabled: !isTeacher,
  });

  const rows = isTeacher ? mine : all;

  return (
    <Layout title="Daily Reports">
      {!isTeacher && (
        <div className="bg-white border border-slate-200 rounded-md p-6 mb-6" data-testid="daily-report-filters">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
            <div className="space-y-1.5">
              <Label>Date</Label>
              <Input type="date" data-testid="daily-report-date-filter" value={date}
                onChange={(e) => setDate(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Department</Label>
              <Select value={deptId} onValueChange={setDeptId}>
                <SelectTrigger data-testid="daily-report-dept-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All departments</SelectItem>
                  {departments.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {(date || deptId !== "all") && (
              <Button variant="ghost" data-testid="daily-report-clear-filters"
                onClick={() => { setDate(""); setDeptId("all"); }}>Clear filters</Button>
            )}
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-md" data-testid="daily-reports-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              {!isTeacher && <TableHead>Teacher</TableHead>}
              <TableHead className="text-right">Allocated</TableHead>
              <TableHead className="text-right">Conducted</TableHead>
              <TableHead>Missed reason</TableHead>
              <TableHead>Submitted</TableHead>
              <TableHead className="w-20"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={isTeacher ? 6 : 7} className="text-center text-slate-400 py-10" data-testid="daily-reports-empty">
                No daily reports yet.
              </TableCell></TableRow>
            )}
            {rows.map((r) => (
              <TableRow key={r.id} data-testid={`daily-report-row-${r.id}`}>
                <TableCell className="font-data">{r.date}</TableCell>
                {!isTeacher && <TableCell className="font-medium">{r.teacher_name}</TableCell>}
                <TableCell className="text-right font-data">{r.allocated_classes}</TableCell>
                <TableCell className="text-right font-data">{r.conducted_classes}</TableCell>
                <TableCell className="max-w-48 truncate text-slate-500">
                  {r.conducted_classes < r.allocated_classes
                    ? r.missed_reason
                    : <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">All conducted</Badge>}
                </TableCell>
                <TableCell className="text-slate-500 text-sm">{fmtIST(r.created_at)}</TableCell>
                <TableCell>
                  <Button variant="ghost" size="icon" data-testid={`view-daily-report-${r.id}`} onClick={() => setView(r)}
                    className="text-slate-400 hover:text-slate-950 hover:bg-slate-100"><Eye className="h-4 w-4" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!view} onOpenChange={() => setView(null)}>
        <DialogContent className="bg-white rounded-md max-w-xl max-h-[85vh] overflow-auto" data-testid="daily-report-dialog">
          <DialogHeader>
            <DialogTitle>Daily report — {view?.teacher_name} · {view?.date}</DialogTitle>
          </DialogHeader>
          {view && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
                  <p className="text-xs text-slate-400">Allocated classes</p>
                  <p className="font-data font-semibold">{view.allocated_classes}</p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
                  <p className="text-xs text-slate-400">Classes conducted</p>
                  <p className="font-data font-semibold">{view.conducted_classes}</p>
                </div>
              </div>
              {FIELDS.map(([key, label]) => (
                <div key={key} className="bg-slate-50 border border-slate-200 rounded-md px-3 py-2" data-testid={`report-field-${key}`}>
                  <p className="text-xs text-slate-400">{label}</p>
                  <p className="text-sm text-slate-800 whitespace-pre-wrap">{view[key] || "—"}</p>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
